import { useState } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { EmptyState } from '@/components/fragments'

/** Chart data shape: month/day + one numeric value key (e.g. applications or value) */
type ChartDataPoint = {
  month?: string
  day?: string
  [valueKey: string]: string | number | undefined
}

interface TrendChartProps {
  title?: string
  total?: string
  trend?: string
  trendClass?: string
  showChart?: boolean
  weeklyChartData?: ChartDataPoint[]
  monthlyChartData?: ChartDataPoint[]
  valueKey?: string
  tooltipLabel?: string
  formatValue?: (value: number) => string
  emptyTitle?: string
  emptyDescription?: string
  onTimePeriodChange?: (period: string) => void
}

export default function TrendChart({
  showChart = false,
  weeklyChartData,
  monthlyChartData,
  valueKey = 'applications',
  tooltipLabel = 'Applications',
  formatValue,
  emptyTitle = 'No Application Trends Data',
  emptyDescription = 'Application trends data will appear here once restaurants start applying.',
  onTimePeriodChange,
}: TrendChartProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<'weekly' | 'month'>('weekly')

  const handlePeriodChange = (value: string) => {
    setSelectedPeriod(value as 'weekly' | 'month')
    onTimePeriodChange?.(value)
  }

  // Use the dataset that matches the selected period
  const isMonthly = selectedPeriod === 'month'
  const chartData = isMonthly ? monthlyChartData : weeklyChartData
  const dataKey = isMonthly ? 'month' : 'day'

  // Check if there's data to display
  const hasData = chartData && chartData.length > 0

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const rawValue = payload[0].value
      const displayValue =
        typeof rawValue === 'number' && formatValue
          ? formatValue(rawValue)
          : rawValue
      return (
        <div className='bg-white border border-gray-200 rounded-lg shadow-lg p-3'>
          <p className='font-semibold text-gray-900'>{label}</p>
          <p className='text-sm text-gray-600'>
            {tooltipLabel}:{' '}
            <span className='font-medium text-[#295532]'>{displayValue}</span>
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <div className='w-full'>
      <div className='w-full flex justify-end items-start mb-4'>
        <Select value={selectedPeriod} onValueChange={handlePeriodChange}>
          <SelectTrigger className='w-[100px] sm:w-[120px] h-8 text-xs'>
            <SelectValue placeholder='Weekly' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='weekly'>Weekly</SelectItem>
            <SelectItem value='month'>Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {showChart && (
        <div className='mt-4 h-48 sm:h-56 md:h-64 lg:h-[20rem] w-full'>
          {hasData ? (
            <ResponsiveContainer width='100%' height='100%'>
              <LineChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 5,
                  left: 0,
                  bottom: isMonthly ? 60 : 0,
                }}
              >
                <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
                <XAxis
                  dataKey={dataKey}
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  className='text-xs sm:text-sm'
                  fontSize={12}
                  angle={isMonthly ? -45 : 0}
                  textAnchor={isMonthly ? 'end' : 'middle'}
                  interval={0}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={4}
                  className='text-xs sm:text-sm'
                  fontSize={12}
                  domain={[0, 'auto']}
                  allowDecimals={false}
                  tickFormatter={
                    formatValue
                      ? (v: number) => formatValue(Number(v))
                      : undefined
                  }
                />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Line
                  type='monotone'
                  dataKey={valueKey}
                  stroke='#295532'
                  strokeWidth={2}
                  dot={isMonthly}
                  activeDot={{ r: 4, fill: '#295532' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState
              variant='data'
              title={emptyTitle}
              description={emptyDescription}
              minHeight='300px'
            />
          )}
        </div>
      )}
    </div>
  )
}
